import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Zap } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { adminRiskConfig } from '@/api/admin';
import { ApiError } from '@/lib/http';
import { toast } from '@/components/ui/sonner';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * 风控规则「立即生效」弹窗 — 绕过次日 03:05,强制把 risk.* 待生效变更立即提前应用。
 * 需 reason(≥4 字),与时段「立即生效」对称(features/session/SessionApplyNowDialog)。
 *
 * 重要:阈值即时生效于下一次风控判定(下单 pre_check / 风控监控 loop 实时读 system_config)。
 * 收紧阈值(下调单日最大亏损 / 上调净值水位下限)可能在下个监控周期立即触发紧急停机;设为 0
 * 表示关闭对应规则。
 */
export function RiskApplyNowDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
    }
  }, [open]);

  const submit = useMutation({
    mutationFn: () => adminRiskConfig.applyPendingNow({ reason: reason.trim() }),
    onSuccess: (res) => {
      toast.success('风控配置已立即生效', {
        description: `已应用 ${res.appliedKeys.length} 项;阈值即时切换,下一次下单 / 监控周期按新阈值判定`,
      });
      qc.invalidateQueries({ queryKey: ['admin', 'risk-config'] });
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error('立即生效失败', { description: err instanceof ApiError ? err.message : '未知错误' });
    },
  });

  const reasonOk = reason.trim().length >= 4;
  const canSubmit = reasonOk && !submit.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submit.isPending && onOpenChange(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-warn">
            <Zap className="h-5 w-5" />
            立即生效 · 风控规则
          </DialogTitle>
          <DialogDescription>
            正常修改于次日 03:05 生效,本操作绕过该机制立即生效。阈值即时作用于下一次风控判定;注意:
            收紧阈值(下调单日最大亏损 / 上调净值水位下限)可能在下个监控周期立即触发紧急停机;设为
            0 表示关闭对应规则。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="risk-apn-reason">操作原因(至少 4 字)</Label>
            <Input
              id="risk-apn-reason"
              placeholder="例如:收紧净值水位下限即时生效"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submit.isPending}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submit.isPending}>
            取消
          </Button>
          <Button variant="warn" onClick={() => submit.mutate()} disabled={!canSubmit}>
            {submit.isPending ? '生效中...' : '立即生效'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
